// Authentication service for AgriManage Pro
// Handles user authentication, role management, and persistence

// Sample user accounts with different roles
const sampleUsers = [
  {
    id: 'u1',
    username: 'admin',
    password: 'admin123', // In a real app, this would be hashed
    name: 'Admin User',
    role: 'admin',
    email: 'admin@agrimanage.com'
  },
  {
    id: 'u2',
    username: 'manager',
    password: 'manager123',
    name: 'Farm Manager',
    role: 'manager',
    email: 'manager@agrimanage.com'
  },
  {
    id: 'w1',
    username: 'worker1',
    password: 'worker123',
    name: 'John Doe',
    role: 'worker',
    email: 'john@agrimanage.com'
  },
  {
    id: 'w2',
    username: 'worker2',
    password: 'worker123',
    name: 'Jane Smith',
    role: 'worker',
    email: 'jane@agrimanage.com'
  },
  {
    id: 'a1',
    username: 'accountant',
    password: 'accountant123',
    name: 'Finance User',
    role: 'accountant',
    email: 'finance@agrimanage.com'
  }
];

// Persistent storage key
const AUTH_STORAGE_KEY = 'agrimanage-auth';

// Helper to initialize user data in local storage
const initializeUsers = () => {
  const existingUsers = localStorage.getItem('agrimanage-users');
  if (!existingUsers) {
    localStorage.setItem('agrimanage-users', JSON.stringify(sampleUsers));
  }
};

// Initialize on import
initializeUsers();

// Get all users (admin function)
export const getAllUsers = () => {
  const users = JSON.parse(localStorage.getItem('agrimanage-users') || '[]');
  // Return users without passwords for security
  return users.map(({ password, ...user }) => user);
};

// Login function
export const login = (username, password) => {
  const users = JSON.parse(localStorage.getItem('agrimanage-users') || '[]');
  
  const user = users.find(
    u => u.username.toLowerCase() === username.toLowerCase() && u.password === password
  );
  
  if (user) {
    // Create session object without password
    const { password, ...userWithoutPassword } = user;
    const session = {
      user: userWithoutPassword,
      isAuthenticated: true,
      loginTime: new Date().toISOString()
    };
    
    // Store in localStorage
    localStorage.setItem(AUTH_STORAGE_KEY, JSON.stringify(session));
    return session;
  }
  
  return null;
};

// Logout function
export const logout = () => {
  localStorage.removeItem(AUTH_STORAGE_KEY);
  return { isAuthenticated: false, user: null };
};

// Get current authenticated user
export const getCurrentUser = () => {
  const authData = localStorage.getItem(AUTH_STORAGE_KEY);
  
  if (!authData) {
    return { isAuthenticated: false, user: null };
  }
  
  try {
    return JSON.parse(authData);
  } catch (e) {
    localStorage.removeItem(AUTH_STORAGE_KEY);
    return { isAuthenticated: false, user: null };
  }
};

// Check if user has specific role
export const hasRole = (role) => {
  const { isAuthenticated, user } = getCurrentUser();
  if (!isAuthenticated || !user) return false;
  
  if (Array.isArray(role)) {
    return role.includes(user.role);
  }
  
  return user.role === role;
};

// Register new user (admin function)
export const registerUser = (newUser) => {
  if (!newUser.username || !newUser.password || !newUser.role) {
    throw new Error('Username, password, and role are required');
  }
  
  const users = JSON.parse(localStorage.getItem('agrimanage-users') || '[]');
  
  // Check for existing username
  if (users.some(u => u.username.toLowerCase() === newUser.username.toLowerCase())) {
    throw new Error('Username already exists');
  }
  
  // Create new user with ID
  const userToAdd = {
    ...newUser,
    id: `u${new Date().getTime()}` // Simple ID generation
  };
  
  // Add to users array
  const updatedUsers = [...users, userToAdd];
  localStorage.setItem('agrimanage-users', JSON.stringify(updatedUsers));
  
  // Return created user without password
  const { password, ...userWithoutPassword } = userToAdd;
  return userWithoutPassword;
};

// Update user information (for profile updates)
export const updateUser = (userId, updates) => {
  const users = JSON.parse(localStorage.getItem('agrimanage-users') || '[]');
  
  const userIndex = users.findIndex(u => u.id === userId);
  if (userIndex === -1) {
    throw new Error('User not found');
  }
  
  // Don't allow updates to role through this function for security
  delete updates.role;
  
  // Update user
  const updatedUser = { ...users[userIndex], ...updates };
  users[userIndex] = updatedUser;
  
  localStorage.setItem('agrimanage-users', JSON.stringify(users));
  
  // If this is the current user, update the session
  const currentAuth = getCurrentUser();
  if (currentAuth.isAuthenticated && currentAuth.user.id === userId) {
    const { password, ...userWithoutPassword } = updatedUser;
    const session = {
      ...currentAuth,
      user: userWithoutPassword
    };
    localStorage.setItem(AUTH_STORAGE_KEY, JSON.stringify(session));
  }
  
  // Return updated user without password
  const { password, ...userWithoutPassword } = updatedUser;
  return userWithoutPassword;
};

// Change user role (admin function)
export const changeUserRole = (userId, newRole) => {
  if (!['admin', 'manager', 'worker', 'accountant'].includes(newRole)) {
    throw new Error('Invalid role');
  }
  
  return updateUser(userId, { role: newRole });
};

// Delete user (admin function)
export const deleteUser = (userId) => {
  const users = JSON.parse(localStorage.getItem('agrimanage-users') || '[]');
  const filteredUsers = users.filter(u => u.id !== userId);
  
  if (filteredUsers.length === users.length) {
    throw new Error('User not found');
  }
  
  localStorage.setItem('agrimanage-users', JSON.stringify(filteredUsers));
  return true;
};

export default {
  login,
  logout,
  getCurrentUser,
  hasRole,
  getAllUsers,
  registerUser,
  updateUser,
  changeUserRole,
  deleteUser
}; 